import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import { useDeepCompareEffect } from 'use-deep-compare';

import {
    IconInfoCircle,
    TableHeader,
    TableHeaderGroup,
    TableRow,
    TruncatedTextCell,
    TruncatedTwoRowCell,
    DateCell,
} from '@trqk/components';
import {
    createFuzzyTextFilter,
    formatYearAndQuarter,
    getScaledColumnWidths,
    getScrollbarWidth,
    locale,
} from '@trqk/utils';
import { useStateWithCallback } from '@trqk/hooks';

import {
    COLUMN_IDS,
    columnWidthInputs,
    columnWidthInputsWithFilename,
    defaultColumnWidth,
    rowHeight,
} from './constants';
import { DeleteCell, DownloadCell, ReportNameCell, EditCell } from './cell';
import {
    getGapSummaryReportDownloadCellValue,
    getGapSummaryReportNameCellValue,
} from './utils';
import {
    useGapAnalysisTools,
    usePaymentPlanStatus,
} from '../../../../../hooks';

interface GapAnalysisReportSummaryTableProps {
    /**
     * Height for the table
     */
    height: number;
    /**
     * Array of {@link TrqkGapSummary} objects to render as table rows
     */
    trqkGapSummaries: TrqkGapSummary[];
    /**
     * Width for the table
     */
    width: number;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of the user's Gap Analysis Reports that have been completed.
 * @param props - {@link GapAnalysisReportSummaryTableProps}
 * @returns JSX
 */
export const GapAnalysisReportSummaryTable = ({
    height,
    trqkGapSummaries,
    width,
}: GapAnalysisReportSummaryTableProps): JSX.Element => {
    const { exportingGapAnalysisIds } = useGapAnalysisTools();
    const { isPublisherEdition } = usePaymentPlanStatus();
    const exportingGapAnalysisIdsString = JSON.stringify(
        exportingGapAnalysisIds,
    );

    const skipAutoResetRef = useRef(false);

    const setSkipAutoResetRefToFalse = useCallback(() => {
        skipAutoResetRef.current = false;
    }, []);

    const [data, setData] = useStateWithCallback(
        trqkGapSummaries,
        setSkipAutoResetRefToFalse,
    );

    useDeepCompareEffect(() => {
        skipAutoResetRef.current = true;

        setData(trqkGapSummaries);
    }, [trqkGapSummaries]);

    const updateMyData: TableDataUpdater = useCallback(
        (itemId, columnId, value) => {
            skipAutoResetRef.current = true;

            setData(prevData =>
                prevData.map(row => {
                    if (
                        row.gap_analysis_id === itemId &&
                        columnId !== undefined
                    ) {
                        return {
                            ...row,
                            [columnId]: value,
                        };
                    }

                    return row;
                }),
            );
        },
        [setData, skipAutoResetRef],
    );

    useEffect(() => {
        skipAutoResetRef.current = false;
    });

    // Calculate table width based on the available space in the viewport. The table will grow
    // horizontally to fill the viewport, but it will not cause the page to scroll.
    const tableElementRef = useRef<HTMLDivElement>(null);

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(() => {
        const scaledColumnWidths = isPublisherEdition
            ? getScaledColumnWidths(width, columnWidthInputsWithFilename)
            : getScaledColumnWidths(width, columnWidthInputs);

        const columnList = [
            {
                id: COLUMN_IDS.REPORT_NAME,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .REPORT_NAME,
                accessor: getGapSummaryReportNameCellValue,
                Cell: ReportNameCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.REPORT_NAME] ??
                    defaultColumnWidth,
            },
            {
                id: COLUMN_IDS.PRO,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS.PRO,
                accessor: ({ pro }: TrqkGapSummary): string => pro,
                Cell: TruncatedTextCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.PRO] ?? defaultColumnWidth,
            },
            {
                id: COLUMN_IDS.PERFORMANCE_PERIOD,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .PERFORMANCE_PERIOD,
                accessor: ({
                    performance_period,
                }: TrqkGapSummary): Maybe<string> =>
                    formatYearAndQuarter(performance_period ?? ''),
                Cell: TruncatedTextCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.PERFORMANCE_PERIOD] ??
                    defaultColumnWidth,
            },
            {
                id: COLUMN_IDS.CHANNELS_INCLUDED,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .CHANNELS_INCLUDED,
                accessor: ({ channel_count }: TrqkGapSummary): number =>
                    channel_count ?? 0,
                Cell: TruncatedTextCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.CHANNELS_INCLUDED] ??
                    defaultColumnWidth,
            },
            {
                id: COLUMN_IDS.DATE_CREATED,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .DATE_CREATED,
                accessor: ({ date_created }: TrqkGapSummary): Maybe<number> => {
                    if (!date_created) {
                        return null;
                    }

                    const unixEpochTime = parseInt(date_created, 10);

                    if (Number.isNaN(unixEpochTime)) {
                        return null;
                    }

                    return unixEpochTime * 1000;
                },
                Cell: DateCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.DATE_CREATED] ??
                    defaultColumnWidth,
            },
            {
                id: COLUMN_IDS.EDIT,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS.EDIT,
                accessor: ({ gap_analysis_id }: TrqkGapSummary): string =>
                    gap_analysis_id,
                Cell: EditCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.EDIT] ?? defaultColumnWidth,
                disableSortBy: true,
            },
            /**
             * 2/20/24 Alex: This column has been removed because we are no longer providing the
             * ability to view the gap analysis report within the app. We think customers are more
             * interested in the ability to download the report and view it in a spreadsheet editor.
             * I'm keeping this here in case we reverse this decision.
             */
            // {
            //     id: COLUMN_IDS.VIEW,
            //     Header: TableHeader,
            //     headerLabel:
            //         locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS.VIEW,
            //     accessor: getGapSummaryReportViewCellValue,
            //     Cell: ViewCell,
            //     width:
            //         scaledColumnWidths?.[COLUMN_IDS.VIEW] ?? defaultColumnWidth,
            //     sortType: (
            //         rowA: Row<TrqkGapSummary>,
            //         rowB: Row<TrqkGapSummary>,
            //     ): number => {
            //         const rowAComplete = rowA?.original?.complete ?? false;
            //         const rowBComplete = rowB?.original?.complete ?? false;

            //         if (rowAComplete && !rowBComplete) {
            //             return 1;
            //         }

            //         if (rowBComplete && !rowAComplete) {
            //             return -1;
            //         }

            //         return 0;
            //     },
            // },
            {
                id: COLUMN_IDS.DOWNLOAD,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .DOWNLOAD,
                accessor: (
                    trqkGapSummary: TrqkGapSummary,
                ): GapSummaryReportDownloadCellValue =>
                    getGapSummaryReportDownloadCellValue({
                        trqkGapSummary,
                        exportingGapAnalysisIdsString,
                    }),
                Cell: DownloadCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.DOWNLOAD] ??
                    defaultColumnWidth,
                sortType: (
                    rowA: Row<TrqkGapSummary>,
                    rowB: Row<TrqkGapSummary>,
                ): number => {
                    const rowAComplete = rowA?.original?.complete ?? false;
                    const rowBComplete = rowB?.original?.complete ?? false;

                    if (rowAComplete && !rowBComplete) {
                        return 1;
                    }

                    if (rowBComplete && !rowAComplete) {
                        return -1;
                    }

                    return 0;
                },
            },
            {
                id: COLUMN_IDS.DELETE,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .DELETE,
                accessor: getGapSummaryReportNameCellValue,
                Cell: DeleteCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.DOWNLOAD] ??
                    defaultColumnWidth,
                sortType: (
                    rowA: Row<TrqkGapSummary>,
                    rowB: Row<TrqkGapSummary>,
                ): number => {
                    const rowAComplete = rowA?.original?.complete ?? false;
                    const rowBComplete = rowB?.original?.complete ?? false;

                    if (rowAComplete && !rowBComplete) {
                        return 1;
                    }

                    if (rowBComplete && !rowAComplete) {
                        return -1;
                    }

                    return 0;
                },
            },
        ];

        if (isPublisherEdition) {
            columnList.splice(1, 0, {
                id: COLUMN_IDS.FILENAME,
                Header: TableHeader,
                headerLabel:
                    locale.enUS.GAP_ANALYSIS.REPORT_SUMMARY_TABLE.HEADERS
                        .FILENAME,
                accessor: ({
                    detection_batch_filename,
                }: TrqkGapSummary): Maybe<string> =>
                    detection_batch_filename ?? null,
                Cell: TruncatedTwoRowCell,
                width:
                    scaledColumnWidths?.[COLUMN_IDS.FILENAME] ??
                    defaultColumnWidth,
            });
        }

        return columnList;
    }, [exportingGapAnalysisIdsString, updateMyData, width]);

    const filterTypes = useMemo(
        () => ({
            titleFilter: createFuzzyTextFilter({
                keys: ['values.track.title'],
            }),
        }),
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
    } = useTable<TrqkGapSummary>(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            initialState: {
                sortBy: [
                    {
                        id: COLUMN_IDS.DATE_CREATED,
                        desc: true,
                    },
                ],
            },
            globalFilter: filterTypes.titleFilter,
            autoResetPage: !skipAutoResetRef.current,
            autoResetExpanded: !skipAutoResetRef.current,
            autoResetGroupBy: !skipAutoResetRef.current,
            autoResetSelectedRows: !skipAutoResetRef.current,
            autoResetSortBy: !skipAutoResetRef.current,
            autoResetFilters: !skipAutoResetRef.current,
            autoResetGlobalFilter: !skipAutoResetRef.current,
            autoResetRowState: !skipAutoResetRef.current,
        },
        useBlockLayout,
        useSortBy,
    );

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            return (
                <TableRow<TrqkGapSummary>
                    index={index}
                    style={style}
                    row={row}
                    textSize="sm"
                />
            );
        },
        [prepareRow, rows],
    );

    const listRef = useRef<HTMLDivElement>(null);

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <>
            <div
                ref={tableElementRef}
                {...getTableProps()}
                className="trqk-table font-normal"
            >
                <div>
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<TrqkGapSummary>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<TrqkGapSummary>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div {...getTableBodyProps()}>
                    {rows.length === 0 && (
                        <div
                            className="flex justify-center items-center italic text-tertiary-400"
                            style={{ height: 350 }}
                        >
                            <div className="flex flex-col items-center justify-center">
                                <div className="flex items-center">
                                    <IconInfoCircle
                                        addClasses="mr-1"
                                        width={20}
                                        fillColor="primary-500"
                                    />
                                    {
                                        locale.enUS.GAP_ANALYSIS
                                            .REPORT_SUMMARY_TABLE
                                            .CURRENT_DATA_EMPTY
                                    }
                                </div>
                            </div>
                        </div>
                    )}
                    {rows.length > 0 && (
                        <FixedSizeList
                            height={height}
                            itemCount={rows.length}
                            itemSize={rowHeight}
                            width={totalColumnsWidth + scrollbarWidth}
                            outerRef={listRef}
                        >
                            {RenderRow}
                        </FixedSizeList>
                    )}
                </div>
            </div>
        </>
    );
};
